/**
 * Feature flags — client-side defaults for in-progress modules.
 *
 * Each flag has a build-time default (this file) plus an optional
 * runtime override via `VITE_FF_<NAME>=true|false`. Runtime overrides
 * let us QA an in-progress slice on staging without redeploying.
 *
 * Why not a remote flag service: one launch customer; LaunchDarkly /
 * Unleash is not justified for two flags. `[LARK-KAI-1]` cancelled
 * the Kai program — do not wait on that doc. Revisit when a second
 * customer onboards.
 *
 * Reference: docs/design-proposals/2026-05-22-tasks-v2-and-weekly-report.md §12.3.
 */

type FlagDef<T extends string> = {
  /** Compile-time default. */
  default: boolean;
  /** Vite env key (must start with `VITE_FF_`). */
  envKey: `VITE_FF_${T}`;
  /** Short human-readable description for the Settings dev panel (Phase 2). */
  description: string;
};

const FLAGS = {
  /**
   * Tasks v2 — per-task chat thread panel. BE messaging endpoints are
   * delivered in a parallel Slice 1.2 BE PR. While that PR is in flight,
   * the messages panel on `/tasks/:id` stays hidden so a 404 on the
   * messages list doesn't paint the screen with an error. Flip to `true`
   * once Slice 1.2 BE ships (or set `VITE_FF_tasksV2Messaging=true` on
   * the dev preview).
   */
  tasksV2Messaging: {
    default: true,
    envKey: 'VITE_FF_tasksV2Messaging',
    description: 'Per-task chat thread on Tasks v2 detail page.',
  } satisfies FlagDef<'tasksV2Messaging'>,

  /**
   * Marketing module v1 (foundation): Connections / Dashboard /
   * Recommendations surface. Provider ingestion ships stubbed (CSV import
   * is live) and the "AI Council" label awaits founder sign-off, so the
   * flag is the no-redeploy kill switch while those settle. Gates the nav
   * item and the /marketing route together.
   */
  marketingModule: {
    default: true,
    envKey: 'VITE_FF_marketingModule',
    description: 'Marketing module: connections, dashboard, AI recommendations.',
  } satisfies FlagDef<'marketingModule'>,
} as const;

export type FeatureFlagKey = keyof typeof FLAGS;

function readEnvFlag(envKey: string): boolean | null {
  // `import.meta.env` may not have arbitrary keys typed, so we cast.
  const raw = (import.meta.env as Record<string, string | undefined>)[envKey];
  if (raw === undefined) return null;
  if (raw === 'true' || raw === '1') return true;
  if (raw === 'false' || raw === '0') return false;
  return null;
}

/**
 * Resolve a feature flag at call time. Runtime env override beats the
 * compile-time default. The function is sync + side-effect free so it
 * is cheap to call inside render.
 */
export function getFeatureFlag(key: FeatureFlagKey): boolean {
  const def = FLAGS[key];
  const override = readEnvFlag(def.envKey);
  return override ?? def.default;
}

/** React-shaped helper for use inside components. */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return getFeatureFlag(key);
}

export const FEATURE_FLAG_DEFINITIONS = FLAGS;
